import React from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { FormInput } from 'src/components/common/forms';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { getPublisherTimezoneOptions } from 'src/components/common/forms/TimezoneSelect';
import { BrandingSelector } from 'src/components/publisher-config/BrandingSelector';
import { BrandBadge } from 'src/components/publisher-config/BrandBadge';
import { useBrandControllerGetAvailablePresetsQuery } from 'src/store/leela-api';
import appConfig from 'src/config';
import type { Control, FieldValues, UseFormWatch, UseFormSetValue } from 'react-hook-form';

import { ConfigurationType } from 'src/constants';

interface PublisherAccountFieldsProps {
  publisher: string;
  /** When provided, renders form inputs. When omitted, renders read-only text. */
  control?: Control<FieldValues>;
  /** Read-only values, used when control is not provided */
  values?: Record<string, unknown>;
  /** Form watch function — required when control is provided (for branding selector). */
  watch?: UseFormWatch<FieldValues>;
  /** Form setValue function — required when control is provided (for branding selector). */
  setValue?: UseFormSetValue<FieldValues>;
}

interface PublisherFieldDescriptor {
  key: string;
  labelKey: string;
  /** Field input type. Defaults to a plain text input; 'timezone' renders an IANA dropdown. */
  type?: 'timezone';
}

// Every publisher account config carries an account-level IANA timezone.
const TIMEZONE_FIELD: PublisherFieldDescriptor = { key: 'timezone', labelKey: 'publisher_config_timezone', type: 'timezone' };

export const FIELD_CONFIG: Record<string, PublisherFieldDescriptor[]> = {
  meta: [{ key: 'catalogId', labelKey: 'publisher_config_catalog_id' }, TIMEZONE_FIELD],
  tiktok: [
    { key: 'catalogId', labelKey: 'publisher_config_catalog_id' },
    { key: 'businessCenterId', labelKey: 'publisher_config_business_center_id' },
    TIMEZONE_FIELD,
  ],
  adsp: [
    { key: 'advertiserId', labelKey: 'publisher_config_advertiser_id' },
    { key: 'userId', labelKey: 'publisher_config_user_id' },
    TIMEZONE_FIELD,
  ],
  google: [
    { key: 'mccAccountId', labelKey: 'publisher_config_mcc_account_id' },
    { key: 'customerAccountId', labelKey: 'publisher_config_customer_account_id' },
    TIMEZONE_FIELD,
  ],
};

/** Chunks fields into rows of two so they render as a clean 2-column grid. */
function toRows (fields: PublisherFieldDescriptor[]): PublisherFieldDescriptor[][] {
  const rows: PublisherFieldDescriptor[][] = [];
  for (let i = 0; i < fields.length; i += 2) {
    rows.push(fields.slice(i, i + 2));
  }
  return rows;
}

export const PublisherAccountFields: React.FC<PublisherAccountFieldsProps> = ({ publisher, control, values, watch, setValue }) => {
  const { t } = useTranslation();
  const { data: brandPresetsData, isLoading: isBrandPresetsLoading } = useBrandControllerGetAvailablePresetsQuery(undefined, { skip: publisher !== 'google' });
  const fields = FIELD_CONFIG[publisher];
  if (!fields) return null;

  const rows = toRows(fields);

  if (control) {
    return (
      <Column spacing="300">
        {rows.map((rowFields, idx) => (
          <Row key={idx} widths={['fill', 'fill']}>
            {rowFields.map(f => (
              f.type === 'timezone'
                ? (
                <FormSelect
                  control={control}
                  data-testid={`publisher-config-${f.key}`}
                  key={f.key}
                  label={t(f.labelKey)}
                  name={f.key}
                  options={getPublisherTimezoneOptions()}
                  required
                  width="100%"
                />
                  )
                : <FormInput control={control} key={f.key} label={t(f.labelKey)} name={f.key} required />
            ))}
            {rowFields.length === 1 && <div />}
          </Row>
        ))}
        {publisher === 'google' && watch && setValue && (
          <BrandingSelector
            disabled={isBrandPresetsLoading}
            mediaBucketName={appConfig.mediaBucketName}
            presets={brandPresetsData?.presets ?? []}
            selectedBrandType={watch('brandType') as string}
            onChange={(brandType: string) => { setValue('brandType', brandType); }}
          />
        )}
      </Column>
    );
  }

  return (
    <Column spacing="300">
      {rows.map((rowFields, idx) => (
        <Row key={idx} widths={['fill', 'fill']}>
          {rowFields.map(f => (
            <Column key={f.key} spacing="100">
              <Text color="secondary" type="b200">{t(f.labelKey)}</Text>
              <Text>{String(values?.[f.key] ?? '')}</Text>
            </Column>
          ))}
          {rowFields.length === 1 && <div />}
        </Row>
      ))}
      {publisher === 'google' && !!values?.brandType && (
        (() => {
          const preset = (brandPresetsData?.presets ?? []).find(p => p.brandType === values.brandType);
          return preset
            ? <BrandBadge brandType={preset.brandType} businessName={preset.businessName} logoS3Key={preset.logoS3Key} mediaBucketName={appConfig.mediaBucketName} />
            : <Text color="secondary">{String(values.brandType)}</Text>;
        })()
      )}
      {publisher === 'google' && (
        <Column spacing="100">
          <Text color="secondary" type="b200">{t('publisher_config_currency')}</Text>
          {/* USD-only for now; falls back to USD when a legacy config has no currency, mirroring the backend default. */}
          <Text>{String(values?.currency ?? 'USD')}</Text>
        </Column>
      )}
    </Column>
  );
};

/** Builds a PUBLISHER_ACCOUNT config object from form data and publisher. */
export function buildPublisherAccountConfig (
  publisher: string,
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const fields = FIELD_CONFIG[publisher] ?? [];
  const config: Record<string, unknown> = {
    configType: ConfigurationType.PUBLISHER_ACCOUNT,
    publisher,
  };
  for (const { key } of fields) {
    config[key] = formData[key];
  }
  // brandType is set by the BrandingSelector card picker, not a form field
  if (formData.brandType) {
    config.brandType = formData.brandType;
  }
  return config;
}
